import React from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router';
import { PageSpinner } from '@/components/Spinner/PageSpinner';
import { useScrollFadeProgress } from '@/hooks/useScrollFadeProgress';
import { Layout } from '@/layouts/Layout';
import { PaymentOptionCard } from '@/pages/Subscription/PaymentOptions/PaymentOptionCard';
import { useStatusCard } from '@/pages/Subscription/hooks/useStatusCard';
import { usePaymentOptions } from '@/pages/Subscription/hooks/usePaymentOptions';
import {
  SubscriptionMobileHeader,
  SubscriptionPageTitle,
} from '@/pages/Subscription/SubscriptionHeader';
import { ROUTE_SUBSCRIPTION } from '@/router/routes';

export const SubscriptionPaymentOptions: React.FC = () => {
  const { t } = useTranslation();
  const { progress: scrollProgress, handleScroll } = useScrollFadeProgress();
  const { canPurchase } = useStatusCard();
  const { data: paymentOptions, isLoading } = usePaymentOptions();

  const disclaimer = t('subscription.paymentOptions.disclaimer', {
    returnObjects: true,
  }) as string[];

  if (!canPurchase) {
    return <Navigate to={ROUTE_SUBSCRIPTION} replace />;
  }

  return (
    <Layout
      disallowPullToRefresh
      hideMobileNavbar
      mobileHeader={
        <SubscriptionMobileHeader
          title={t('subscription.paymentOptions.title')}
          scrollProgress={scrollProgress}
        />
      }
      onScroll={handleScroll}
    >
      {isLoading && (
        <div className="flex min-h-[75vh] items-center justify-center">
          <PageSpinner />
        </div>
      )}

      {!isLoading && (
        <div className="flex flex-col gap-4 px-4 pb-8 md:px-6 md:pt-6">
          <SubscriptionPageTitle
            title={t('subscription.paymentOptions.title')}
            scrollProgress={scrollProgress}
          />
          <p className="text-body-18 text-subtle-foreground">
            {t('subscription.paymentOptions.subtitle')}
          </p>

          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
            {paymentOptions?.map((option) => (
              <PaymentOptionCard key={option.type} option={option} />
            ))}
          </div>

          <ul className="flex list-disc flex-col gap-1 pl-5 text-body-14 text-[#858B99]">
            {disclaimer.map((line, index) => (
              <li key={index}>{line}</li>
            ))}
          </ul>
        </div>
      )}
    </Layout>
  );
};
